/**
 * GFG-273 (Campaigns tab, GFG Admin) — the tab itself: the controls bar (search + filter chips,
 * Columns, Export CSV), the table, and every dialog the row menu can open plus the toast that
 * reports the outcome of a mutation.
 *
 * This is the only unit that calls the mutation hooks; the row menu and the modals report what the
 * user asked for and never touch the API. There is NO optimistic update: `useAdminCampaignActions`
 * invalidates the `["admin", "campaigns"]` prefix on success only, so a refused mutation leaves the
 * row exactly as it was and an accepted one refetches the table.
 */
import { FileDownloadOutlined, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Button, IconButton, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import CampaignRowMenu from "@/components/admin/campaigns/CampaignRowMenu";
import CampaignsTable from "@/components/admin/campaigns/CampaignsTable";
import ClientContactPopover from "@/components/admin/campaigns/ClientContactPopover";
import {
  buildFixedColumns,
  CONFIGURABLE_COLUMNS,
} from "@/components/admin/campaigns/campaignsColumns";
import BlotterFilterBar, {
  type AppliedFilter,
  type FilterField,
  type FilterOption,
} from "@/components/BlotterFilterBar";
import ColumnConfigPopover, { type ColumnConfigItem } from "@/components/ColumnConfigPopover";
import CancelCampaignModal from "@/components/campaigns/lifecycle/CancelCampaignModal";
import ManageContactsModal from "@/components/campaigns/ManageContactsModal";
import GfgModal from "@/components/GfgModal";
import GiftLinksModal from "@/components/giftLinks/GiftLinksModal";
import {
  ADMIN_CAMPAIGN_COLUMN_IDS,
  type AdminCampaignColumnId,
  COLUMN_LABELS,
  COPY,
  FILTER_IDS,
  PAGE_SIZE,
} from "@/constants/adminCampaigns";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import {
  ADMIN_CAMPAIGN_ENDPOINTS,
  CampaignEndpointsContext,
} from "@/context/CampaignEndpointsContext";
import { useAdminCampaignActions, useAdminCampaigns } from "@/hooks/useAdminCampaigns";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useImpersonateCampaign } from "@/hooks/useImpersonate";
import {
  ADMIN_CAMPAIGN_SEND_METHODS,
  ADMIN_CAMPAIGN_STATUSES,
  ADMIN_CAMPAIGN_TYPES,
  type AdminCampaignRow,
  type AdminCampaignSortKey,
} from "@/interfaces/adminCampaigns";
import { downloadCsv } from "@/lib/csv";
import { buildCampaignsCsv, toClientStatus, toListParams } from "@/utils/adminCampaigns-helper";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { applyPrefs } from "@/utils/columnPrefs-helper";

/** Mirrors the prefix hooks/useAdminCampaigns.ts keys every request under — what Retry clears. */
const ADMIN_CAMPAIGNS_QUERY_KEY = ["admin", "campaigns"];

/** The search param holding the column arrangement, so a configured table is a shareable link. */
const COLS_PARAM = "cols";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Copy only this panel renders. Local for the same reason AdminContactsPanel keeps its own
 * FAILURE_COPY local — nothing else reads it; every string more than one unit shares is in
 * constants/adminCampaigns.ts.
 */
const PANEL_COPY = {
  exportCsv: "Export CSV",
  csvFilename: "gfg-campaigns.csv",
  columnsHint: "Action, Campaign Name and Client are always shown.",
  listFailed: "We couldn't load campaigns. Check your connection and try again.",
  retry: "Retry",
  pagerEmpty: "0 campaigns",
  pagerRange: (start: number, end: number, total: number) =>
    `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`,
  previousPage: "Previous page",
  nextPage: "Next page",
} as const;

/**
 * The three vocabulary filters are built from the frontend's own contract rather than fetched:
 * their values are fixed and already known here, so a failed request cannot empty a list that
 * needs no round trip. Only Client/Org is reference data and rides on the list response.
 */
const STATUS_OPTIONS: FilterOption[] = ADMIN_CAMPAIGN_STATUSES.map((v) => ({ value: v, label: v }));
const TYPE_OPTIONS: FilterOption[] = ADMIN_CAMPAIGN_TYPES.map((v) => ({ value: v, label: v }));
const SEND_METHOD_OPTIONS: FilterOption[] = ADMIN_CAMPAIGN_SEND_METHODS.map((v) => ({
  value: v,
  label: v,
}));

/** Rows of the Columns popover: the 13 configurable ids. The three fixed ones are absent by
 *  design — they cannot be hidden or reordered, and `columnsHint` says so inside the popover. */
const COLUMN_ITEMS: readonly ColumnConfigItem<AdminCampaignColumnId>[] =
  ADMIN_CAMPAIGN_COLUMN_IDS.map((id) => ({ id, label: COLUMN_LABELS[id] }));

/** All 13 start visible; module-level so the prefs hook sees a stable value. */
const NO_HIDDEN_COLUMNS: readonly AdminCampaignColumnId[] = [];

/** What the footer and the pager read before the first envelope ever lands. */
const INITIAL_PAGE_META = { total: 0, totalPages: 1, limit: PAGE_SIZE };

/** Which dialog is open, if any. Opened from a row, one at a time. */
type CampaignsDialog = "contacts" | "giftLinks" | "cancel" | "pause" | "archive";

interface ToastState {
  message: string;
  severity: "success" | "error";
}

export default function CampaignsPanel() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [sortBy, setSortBy] = useState<AdminCampaignSortKey>("scheduled_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState(INITIAL_PAGE_META);
  // The row the menu, the popover or a dialog is about, plus the element each is anchored on.
  // Menu and popover are independent: clicking a Client cell must not disturb an open row menu.
  const [menuRow, setMenuRow] = useState<AdminCampaignRow | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [clientRow, setClientRow] = useState<AdminCampaignRow | null>(null);
  const [clientAnchor, setClientAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<CampaignsDialog | null>(null);
  const [dialogRow, setDialogRow] = useState<AdminCampaignRow | null>(null);
  // Visibility and content are separate state so closing the toast doesn't blank the wording
  // mid-fade; the last message simply holds until the next action replaces it.
  const [toast, setToast] = useState<ToastState | null>(null);
  const [toastOpen, setToastOpen] = useState(false);

  const queryClient = useQueryClient();
  const actions = useAdminCampaignActions();
  const { impersonateCampaign } = useImpersonateCampaign();
  const prefs = useColumnPrefs<AdminCampaignColumnId>({
    paramName: COLS_PARAM,
    allIds: ADMIN_CAMPAIGN_COLUMN_IDS,
    defaultOrder: ADMIN_CAMPAIGN_COLUMN_IDS,
    defaultHidden: NO_HIDDEN_COLUMNS,
  });

  // Debounced so typing doesn't fire a request per keystroke, same 300ms as the Blotter. A new
  // search narrows the set, so page 2 of the old one would read as empty — every narrowing resets.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError } = useAdminCampaigns(
    toListParams(search, filters, sortBy, sortDir, page),
  );

  // Search, filters, sort and paging are all the server's: these rows are one page, already ordered.
  const rows = data?.data ?? [];

  // `page` is part of the query key, so `data` is undefined while the next page is in flight. The
  // last envelope's numbers stand in until the new one lands, so the footer never flashes
  // "0 campaigns" and Next never greys out mid-click.
  if (data !== undefined) {
    const next = {
      total: data.total,
      totalPages: data.totalPages,
      // The server echoes the page size it actually applied; PAGE_SIZE is only the fallback.
      limit: data.limit || PAGE_SIZE,
    };
    if (
      next.total !== pageMeta.total ||
      next.totalPages !== pageMeta.totalPages ||
      next.limit !== pageMeta.limit
    ) {
      setPageMeta(next);
    }
  }

  const { total, totalPages, limit } = pageMeta;
  // A refetch can shrink `total` with no UI event (every mutation invalidates the list, and
  // react-query refetches on window focus), stranding the user on a page that no longer exists.
  // Clamped during render rather than in an effect, which would fire one request for the stale page.
  const lastPage = Math.max(1, totalPages);
  if (!isLoading && page > lastPage) setPage(lastPage);

  const pageStart = (page - 1) * limit + 1;
  const pageEnd = Math.min(page * limit, total);

  // The user's arrangement, projected by the shared helper — the table and the CSV both read it,
  // so the file can never hold a column the screen doesn't, or hold it in another order.
  const visibleColumns = applyPrefs(CONFIGURABLE_COLUMNS, prefs);
  const visibleColumnIds = visibleColumns.map((column) => column.id);

  const showToast = useCallback((message: string, severity: ToastState["severity"]) => {
    setToast({ message, severity });
    setToastOpen(true);
  }, []);

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  const openDialog = useCallback((kind: CampaignsDialog, row: AdminCampaignRow) => {
    setMenuAnchor(null);
    setDialogRow(row);
    setDialog(kind);
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogRow(null);
  }, []);

  const handleOpenMenu = useCallback((row: AdminCampaignRow, anchor: HTMLElement) => {
    setMenuRow(row);
    setMenuAnchor(anchor);
  }, []);

  const handleOpenClient = useCallback((row: AdminCampaignRow, anchor: HTMLElement) => {
    setClientRow(row);
    setClientAnchor(anchor);
  }, []);

  const closeClientPopover = useCallback(() => {
    setClientAnchor(null);
    setClientRow(null);
  }, []);

  /** Clicking the sorted column flips it; clicking another one takes over, ascending first. */
  const handleSort = useCallback(
    (key: AdminCampaignSortKey) => {
      setPage(1);
      if (key === sortBy) {
        setSortDir((current) => (current === "asc" ? "desc" : "asc"));
        return;
      }
      setSortBy(key);
      setSortDir("asc");
    },
    [sortBy],
  );

  const handleFiltersChange = useCallback((next: AppliedFilter[]) => {
    setFilters(next);
    setPage(1);
  }, []);

  /**
   * Retry invalidates the whole `["admin", "campaigns"]` branch, so the list refetches with the
   * search, filters and columns the user is retrying with. `isError` flips back on success, which
   * clears the banner by itself.
   */
  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_CAMPAIGNS_QUERY_KEY });
  }, [queryClient]);

  /** The file holds the rows on screen and the columns on screen, in the order they are shown. */
  const handleExport = useCallback(() => {
    downloadCsv(PANEL_COPY.csvFilename, buildCampaignsCsv(rows, visibleColumnIds));
  }, [rows, visibleColumnIds]);

  /** Every mutation path: the success toast on the server's yes, its own sentence on a no. */
  const runAction = useCallback(
    async (run: () => Promise<unknown>, successMessage: string) => {
      try {
        await run();
        showToast(successMessage, "success");
      } catch (error: unknown) {
        showToast(apiErrorMessage(error), "error");
      }
    },
    [showToast],
  );

  const handleImpersonate = useCallback(
    (row: AdminCampaignRow) => {
      closeMenu();
      if (!row.impersonation.allowed) return;
      void runAction(() => impersonateCampaign(row.id), COPY.toast.impersonating(row.name));
    },
    [closeMenu, impersonateCampaign, runAction],
  );

  const handlePause = useCallback(
    (row: AdminCampaignRow) => {
      if (row.status === CAMPAIGN_STATUSES.ADMIN_ACTIVE) {
        openDialog("pause", row);
        return;
      }
      closeMenu();
      void runAction(() => actions.pause.mutateAsync(row.id), COPY.toast.paused(row.name));
    },
    [actions, closeMenu, openDialog, runAction],
  );

  const confirmPause = useCallback(
    (row: AdminCampaignRow) => {
      closeDialog();
      void runAction(() => actions.pause.mutateAsync(row.id), COPY.toast.paused(row.name));
    },
    [actions, closeDialog, runAction],
  );

  const handleResume = useCallback(
    (row: AdminCampaignRow) => {
      closeMenu();
      void runAction(() => actions.resume.mutateAsync(row.id), COPY.toast.resumed(row.name));
    },
    [actions, closeMenu, runAction],
  );

  /** The client's own Cancel dialog (Ivan, GFG-358) confirms, with its money breakdown. */
  const handleCancel = useCallback(
    (row: AdminCampaignRow) => {
      closeDialog();
      void runAction(() => actions.cancel.mutateAsync(row.id), COPY.toast.cancelled(row.name));
    },
    [actions, closeDialog, runAction],
  );

  const handleDisable = useCallback(
    (row: AdminCampaignRow) => {
      closeMenu();
      void runAction(() => actions.disable.mutateAsync(row.id), COPY.toast.disabled(row.name));
    },
    [actions, closeMenu, runAction],
  );

  const handleEnable = useCallback(
    (row: AdminCampaignRow) => {
      closeMenu();
      void runAction(() => actions.enable.mutateAsync(row.id), COPY.toast.enabled(row.name));
    },
    [actions, closeMenu, runAction],
  );

  const handleArchive = useCallback(
    (row: AdminCampaignRow) => {
      openDialog("archive", row);
    },
    [openDialog],
  );

  const confirmArchive = useCallback(
    (row: AdminCampaignRow) => {
      closeDialog();
      void runAction(() => actions.archive.mutateAsync(row.id), COPY.toast.archived(row.name));
    },
    [actions, closeDialog, runAction],
  );

  const handleRestore = useCallback(
    (row: AdminCampaignRow) => {
      closeMenu();
      void runAction(() => actions.restore.mutateAsync(row.id), COPY.toast.restored(row.name));
    },
    [actions, closeMenu, runAction],
  );

  const columns = [
    ...buildFixedColumns({
      onOpenCampaign: handleImpersonate,
      onOpenClient: handleOpenClient,
      onOpenMenu: handleOpenMenu,
      openMenuRowId: menuAnchor === null ? null : (menuRow?.id ?? null),
      openClientRowId: clientAnchor === null ? null : (clientRow?.id ?? null),
    }),
    ...visibleColumns,
  ];

  const filterFields: FilterField[] = [
    {
      id: FILTER_IDS.organization,
      label: COPY.filterLabels.organization,
      // Empty until the first page lands, and empty if it failed — which the banner says out loud.
      options: data?.filterOptions?.organizations ?? [],
    },
    { id: FILTER_IDS.status, label: COPY.filterLabels.status, options: STATUS_OPTIONS },
    { id: FILTER_IDS.campaignType, label: COPY.filterLabels.campaignType, options: TYPE_OPTIONS },
    {
      id: FILTER_IDS.sendMethod,
      label: COPY.filterLabels.sendMethod,
      options: SEND_METHOD_OPTIONS,
    },
    {
      id: FILTER_IDS.scheduled,
      label: COPY.filterLabels.scheduledDate,
      options: [],
      kind: "daterange",
    },
    {
      id: FILTER_IDS.expiry,
      label: COPY.filterLabels.expirationDate,
      options: [],
      kind: "daterange",
    },
  ];

  return (
    <>
      {isError && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {PANEL_COPY.retry}
            </Button>
          }
        >
          {PANEL_COPY.listFailed}
        </Alert>
      )}

      {/* Wraps rather than crushing: on a narrow screen the filter bar and the two buttons cannot
          share a line, and flex would otherwise shrink them past their own content. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <BlotterFilterBar
          placeholder={COPY.searchPlaceholder}
          search={searchInput}
          onSearchChange={setSearchInput}
          fields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
        <ColumnConfigPopover
          items={COLUMN_ITEMS}
          order={prefs.order}
          hidden={prefs.hidden}
          fixedHint={PANEL_COPY.columnsHint}
          onToggle={prefs.toggle}
          onMove={prefs.move}
          onReorder={prefs.reorder}
          onReset={prefs.reset}
        />
        <Button
          onClick={handleExport}
          startIcon={<FileDownloadOutlined />}
          sx={{
            border: "1px solid var(--color-dark-dust)",
            borderRadius: "var(--radius-btn)",
            color: "var(--color-dark-dust)",
            height: 48,
            px: 2,
            fontFamily: "var(--font-lato)",
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "0.08em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {PANEL_COPY.exportCsv}
        </Button>
      </Box>

      <CampaignsTable
        rows={rows}
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      {/* Same pager the Blotter draws, rebuilt here because this tab deliberately doesn't use it. */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 2 }}
      >
        <Box sx={{ fontSize: 13, color: "var(--color-graphite)", mr: 1 }}>
          {total === 0 ? PANEL_COPY.pagerEmpty : PANEL_COPY.pagerRange(pageStart, pageEnd, total)}
        </Box>
        <IconButton
          size="small"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          aria-label={PANEL_COPY.previousPage}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= totalPages}
          onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          aria-label={PANEL_COPY.nextPage}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>

      {menuRow && (
        <CampaignRowMenu
          campaign={menuRow}
          anchorEl={menuAnchor}
          onClose={closeMenu}
          onImpersonate={() => handleImpersonate(menuRow)}
          onManageContacts={() => openDialog("contacts", menuRow)}
          onManageGiftLinks={() => openDialog("giftLinks", menuRow)}
          onPause={() => handlePause(menuRow)}
          onResume={() => handleResume(menuRow)}
          onCancel={() => openDialog("cancel", menuRow)}
          onDisable={() => handleDisable(menuRow)}
          onEnable={() => handleEnable(menuRow)}
          onArchive={() => handleArchive(menuRow)}
          onRestore={() => handleRestore(menuRow)}
        />
      )}

      {clientRow && (
        <ClientContactPopover
          campaign={clientRow}
          anchorEl={clientAnchor}
          onClose={closeClientPopover}
        />
      )}

      {/* The client's own dialogs, pointed at the Campaign Manager's routes. */}
      <CampaignEndpointsContext.Provider value={ADMIN_CAMPAIGN_ENDPOINTS}>
        {dialogRow && (
          <ManageContactsModal
            open={dialog === "contacts"}
            onClose={closeDialog}
            campaignId={String(dialogRow.id)}
            campaignName={dialogRow.name}
            campaignStatus={toClientStatus(dialogRow.status)}
            manual={dialogRow.kind === "manual"}
            paused={dialogRow.status === CAMPAIGN_STATUSES.ADMIN_PAUSED}
            canEdit={dialogRow.allowedActions.manageContacts === "edit"}
            canSend={dialogRow.allowedActions.manageContacts === "edit"}
            replacementOnly
          />
        )}

        {dialogRow && dialog === "giftLinks" && (
          <GiftLinksModal
            open
            onClose={closeDialog}
            campaignId={String(dialogRow.id)}
            campaignName={dialogRow.name}
            campaignStatus={toClientStatus(dialogRow.status)}
            paused={dialogRow.status === CAMPAIGN_STATUSES.ADMIN_PAUSED}
            canEdit={dialogRow.allowedActions.manageGiftLinks === "edit"}
            replacementOnly
          />
        )}

        {dialogRow && (
          <CancelCampaignModal
            open={dialog === "cancel"}
            campaignId={String(dialogRow.id)}
            campaignName={dialogRow.name}
            onClose={closeDialog}
            onConfirm={() => handleCancel(dialogRow)}
          />
        )}
      </CampaignEndpointsContext.Provider>

      {dialogRow && dialog === "pause" && (
        <GfgModal
          open
          onClose={closeDialog}
          onPrimary={() => confirmPause(dialogRow)}
          icon={<WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />}
          title={COPY.confirmModal.pauseTitle}
          description={COPY.pauseLiveConfirm}
          primaryLabel={COPY.confirmModal.pauseAction}
          secondaryLabel={COPY.confirmModal.dismiss}
        />
      )}

      {dialogRow && dialog === "archive" && (
        <GfgModal
          open
          onClose={closeDialog}
          onPrimary={() => confirmArchive(dialogRow)}
          icon={<WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />}
          title={COPY.confirmModal.archiveTitle}
          description={COPY.archiveConfirm(dialogRow.name)}
          primaryLabel={COPY.confirmModal.archiveAction}
          secondaryLabel={COPY.confirmModal.dismiss}
        />
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity ?? "success"}
          variant="filled"
          onClose={() => setToastOpen(false)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
